import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { EMAIL_REGEX } from '@monofront/business/constants';
import Button from '@monofront/design-system/components/Button';
import Modal from '@monofront/design-system/organisms/Modal';
import Input from '@monofront/design-system/components/Form/Input';
import { useNotifyMe } from '@monofront/business/hooks';
import { useTranslations } from 'next-intl';

interface IModalNotifyMeProps {
  title: string;
  isOpen: boolean;
  externalToggle: () => void;
  sku: string;
}

const ModalNotifyMe: React.FC<IModalNotifyMeProps> = ({
  title,
  isOpen,
  externalToggle,
  sku,
}) => {
  const { loading, notifyMe } = useNotifyMe();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const tCatalog = useTranslations('Catalog');
  const tAccount = useTranslations('Account');

  const submitForm = async ({ email }: Record<string, any>): Promise<void> => {
    await notifyMe({
      email,
      sku,
      successCallback: (messageKey) => {
        externalToggle();
        toast(tCatalog(messageKey));
      },
      errorCallback: (errorMessageKey) => {
        setError('form', {
          type: 'serverError',
          message: tCatalog(errorMessageKey),
        });
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        externalToggle={externalToggle}
        UNSAFE_className="modal-notification"
        hasPadding
      >
        <h3>{title}</h3>
        <p>{tCatalog('notifyMeDetails')}</p>
        <form onSubmit={handleSubmit(submitForm)}>
          {errors?.email?.message && (
            <p className="error tw-lf-error-message">
              {errors?.email?.message as string}
            </p>
          )}
          {errors?.form?.message && (
            <p className="error tw-lf-error-message">
              {errors?.form?.message as string}
            </p>
          )}
          <Input
            type="email"
            placeholder="Email"
            {...register('email', {
              required: true,
              pattern: {
                value: EMAIL_REGEX,
                message: tAccount('useValidEmail'),
              },
            })}
            dataTestId="ModalNotifyMe-Input-1"
          />
          <Button
            type="submit"
            isLoading={loading}
            disabled={loading}
            dataTestId="ModalNotifyMe-Button-submit"
          >
            {tCatalog('notifyMe')}
          </Button>
        </form>
      </Modal>
      <style jsx global>{`
        .modal-notification {
          top: 33%;
          max-width: 460px;
        }

        .modal-notification :global(form) {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .modal-notification :global(h3),
        .modal-notification :global(p),
        .modal-notification :global(input) {
          margin-bottom: var(--space-5);
        }

        .modal-notification :global(button) {
          width: fit-content;
        }

        @media (max-width: 800px) {
          .modal-notification {
            top: 10%;
          }
        }
      `}</style>
    </>
  );
};

export default ModalNotifyMe;
